import { featureToggles } from '../models/feature-toggles';
import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AuthEvent,
  AuthService,
  ClientEnvironment,
  EnvConfigService,
  GlobalContextConfigService,
} from '@openmfp/portal-ui-lib';
import { filter, firstValueFrom, take } from 'rxjs';

export function initAnalyticsTracker() {
  _initAnalyticsTracker(
    inject(EnvConfigService),
    inject(AuthService),
    inject(GlobalContextConfigService),
    inject(ActivatedRoute),
  ).catch();
  return () => undefined;
}

// this is exported only for testing
export async function _initAnalyticsTracker(
  envConfigService: EnvConfigService,
  authService: AuthService,
  globalContextConfigService: GlobalContextConfigService,
  activatedRoute: ActivatedRoute,
): Promise<void> {
  await firstValueFrom(
    authService.authEvents.pipe(
      filter((event) => event === AuthEvent.AUTH_SUCCESSFUL),
      take(1),
    ),
  );
  const envConfig =
    (await envConfigService.getEnvConfig()) as ClientEnvironment & {
      analyticsTrackerEnabled: boolean;
      analyticsTrackerConfig: AnalyzerTrackingConfig;
    };

  if (!envConfig['analyticsTrackerEnabled']) {
    return;
  }
  const hashedUserId = await digestMessage(authService.getUserInfo().userId);
  const analyticsConfig: AnalyzerTrackingConfig = {
    ...envConfig['analyticsTrackerConfig'],
  };

  const globalCtx = await globalContextConfigService.getGlobalContext();
  const tenantId = globalCtx.portalContext.tenantId;

  const analyticsTestEnabled = (activatedRoute.snapshot.queryParams.ft ?? '')
    .split(',')
    .includes(featureToggles.ENABLE_ANALYTICS_TEST);

  if (!analyticsTestEnabled && !analyticsConfig.tenantIds?.includes(tenantId)) {
    return;
  }

  const script = getInitializeScript({
    siteUrl: analyticsConfig.siteUrl,
    matomoContainerId: analyticsConfig.jukeboxMatomoContainerId,
    hashedUserId,
  });
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.text = script;
  document.body.appendChild(scriptElement);
}

async function digestMessage(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  // convert bytes to hex string
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface AnalyzerTrackingConfig {
  siteUrl?: string;
  jukeboxMatomoContainerId?: string;
  tenantIds?: string[];
}

interface MatomoConfig {
  siteUrl?: string;
  matomoContainerId?: string;
  hashedUserId?: string;
}

export function getInitializeScript(config: MatomoConfig): string {
  const url = `${config.siteUrl}js/container_${config.matomoContainerId}.js`;

  return `
      var _mtm = window._mtm = window._mtm || [];
  _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
  _mtm.push({uid: '${config.hashedUserId}' });
  (function() {
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.async=true; g.src='${url}'; s.parentNode.insertBefore(g,s);
  })();
  `;
}
